interface PixelReader {
    gl: WebGL2RenderingContext;
    texture: WebGLTexture;
    framebuffer: WebGLFramebuffer;
}

let reader: PixelReader | null = null;
let readerError: Error | null = null;

function getReader(): PixelReader {
    if (readerError) throw readerError;
    if (reader) return reader;

    try {
        const canvas = document.createElement("canvas");
        const gl = canvas.getContext("webgl2", {
            alpha: true,
            antialias: false,
            depth: false,
            stencil: false,
            premultipliedAlpha: false,
        });
        if (!gl) throw new Error("no WebGL2 context for pixel readback");

        const texture = gl.createTexture();
        const framebuffer = gl.createFramebuffer();
        if (!texture || !framebuffer) throw new Error("failed to allocate pixel readback resources");

        gl.bindTexture(gl.TEXTURE_2D, texture);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

        reader = { gl, texture, framebuffer };
        return reader;
    } catch (error) {
        // Do not keep attempting to create contexts
        readerError = error instanceof Error ? error : new Error(String(error));
        throw readerError;
    }
}

// Read an ImageBitmap without passing its packed channels
// through Canvas 2D's color-managed drawImage/getImageData path
export function readImageBitmapPixels(bitmap: ImageBitmap): Uint8Array {
    const { gl, texture, framebuffer } = getReader();
    if (gl.isContextLost()) throw new Error("pixel readback WebGL2 context is lost");

    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
    gl.pixelStorei(gl.UNPACK_COLORSPACE_CONVERSION_WEBGL, gl.NONE);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, gl.RGBA, gl.UNSIGNED_BYTE, bitmap);

    gl.bindFramebuffer(gl.FRAMEBUFFER, framebuffer);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, texture, 0);
    if (gl.checkFramebufferStatus(gl.FRAMEBUFFER) !== gl.FRAMEBUFFER_COMPLETE) {
        throw new Error("pixel readback framebuffer is incomplete");
    }

    const pixels = new Uint8Array(bitmap.width * bitmap.height * 4);
    gl.readPixels(0, 0, bitmap.width, bitmap.height, gl.RGBA, gl.UNSIGNED_BYTE, pixels);
    const error = gl.getError();
    if (error !== gl.NO_ERROR) throw new Error(`pixel readback failed (${error})`);
    return pixels;
}
